import axios from "axios";
import router from "../../router-config";
import store from "../../store";

const API_PREFIX = "/web/admin/dispatch";
const AUTH_ERROR_CODES = new Set([9004, 9006, 9007, 9202, 401, 403]);
let authWarnAt = 0;
let authRedirecting = false;

function isAuthErrorCode(code) {
  const n = Number(code);
  return AUTH_ERROR_CODES.has(n);
}

function redirectToLogin(message = "未登录或登录已过期，请重新登录") {
  if (store.state.isLogin) {
    store.commit("setLoginOut");
  }
  const currentPath = (router.currentRoute && router.currentRoute.fullPath) || "/dispatch/orders";
  if (router.currentRoute && router.currentRoute.path === "/login") {
    return;
  }
  // 避免在请求链中多次重复跳转
  if (!authRedirecting) {
    authRedirecting = true;
    setTimeout(() => {
      router.replace({
        path: "/login",
        query: { redirect: currentPath },
      });
      setTimeout(() => {
        authRedirecting = false;
      }, 800);
    }, 0);
  }
  const now = Date.now();
  if (typeof window !== "undefined" && window.vue && window.vue.$message && now - authWarnAt > 3000) {
    window.vue.$message.warning(message);
    authWarnAt = now;
  }
}

function unwrapResponse(res) {
  const body = res && res.data;
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return { ok: false, message: "服务无响应" };
  }
  if (isAuthErrorCode(body.code)) {
    redirectToLogin(body.msg || "未登录");
    return { ok: false, message: body.msg || "未登录" };
  }
  if (body.success || body.code === 1) {
    return { ok: true, data: body.data, message: body.msg };
  }
  return { ok: false, message: body.msg || "请求失败" };
}

function normalizeError(error) {
  if (error && error.response && isAuthErrorCode(error.response.status)) {
    redirectToLogin((error.response.data && error.response.data.msg) || "未登录");
  }
  return error;
}

export const dispatchApi = {
  async pageOrders(params = {}) {
    try {
      const res = await axios.get(`${API_PREFIX}/orders`, { params });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || { records: [], total: 0, current: 1, size: 10 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async stats() {
    try {
      const res = await axios.get(`${API_PREFIX}/stats`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || { pending: 0, serving: 0, onlineCaregivers: 0, doneToday: 0 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async dashboard() {
    try {
      const res = await axios.get(`${API_PREFIX}/dashboard`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async listCaregivers() {
    try {
      const res = await axios.get(`${API_PREFIX}/caregivers`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || [];
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async pageCaregivers(params = {}) {
    try {
      const res = await axios.get(`${API_PREFIX}/caregivers/page`, { params });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || { records: [], total: 0, current: 1, size: 10 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async caregiverDetail(id) {
    try {
      const res = await axios.get(`${API_PREFIX}/caregivers/${id}`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async createCaregiver(payload) {
    try {
      const res = await axios.post(`${API_PREFIX}/caregivers`, payload);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async updateCaregiver(id, payload) {
    try {
      const res = await axios.put(`${API_PREFIX}/caregivers/${id}`, payload);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async deleteCaregiver(id) {
    try {
      const res = await axios.delete(`${API_PREFIX}/caregivers/${id}`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async uploadCaregiverFile(file) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_PREFIX}/caregivers/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async assignOrder(orderId, caregiverId) {
    try {
      const res = await axios.post(`${API_PREFIX}/orders/${orderId}/assign`, { caregiverId });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async completeOrder(orderId) {
    try {
      const res = await axios.post(`${API_PREFIX}/orders/${orderId}/complete`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async orderDetail(orderId) {
    try {
      const res = await axios.get(`${API_PREFIX}/orders/${orderId}`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async pageHospitals(params = {}) {
    try {
      const res = await axios.get(`${API_PREFIX}/hospitals/page`, { params });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || { records: [], total: 0, current: 1, size: 10 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async createHospital(payload) {
    try {
      const res = await axios.post(`${API_PREFIX}/hospitals`, payload);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async updateHospital(id, payload) {
    try {
      const res = await axios.put(`${API_PREFIX}/hospitals/${id}`, payload);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async deleteHospital(id) {
    try {
      const res = await axios.delete(`${API_PREFIX}/hospitals/${id}`);
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async pageFamilyUsers(params = {}) {
    try {
      const res = await axios.get(`${API_PREFIX}/family-users`, { params });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || { records: [], total: 0 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async updateFamilyUserState(id, state) {
    try {
      const res = await axios.put(`${API_PREFIX}/family-users/${id}/state`, { state });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async pageAdminUsers(params = {}) {
    try {
      const res = await axios.get(`${API_PREFIX}/sys-users`, {
        params: {
          name: params.search_name || undefined,
          username: params.search_username || undefined,
          state: params.state,
          current: params.current || 1,
          size: params.size || 10,
        },
      });
      const result = unwrapResponse(res);
      if (!result.ok) throw new Error(result.message);
      return result.data || { records: [], total: 0 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async createAdminUser(payload) {
    try {
      const res = await axios.post("/web/users/insert", payload);
      if (typeof res.data === "string" && res.data.indexOf("弱密码") !== -1) {
        throw new Error(res.data);
      }
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async updateAdminUser(payload) {
    try {
      const res = await axios.post("/web/users/update", payload);
      if (res.data !== "修改成功") {
        throw new Error(res.data || "修改失败");
      }
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async resetAdminPassword(userId, username, password) {
    try {
      const res = await axios.post(`/web/users/updPassWord/${userId}`, {
        username,
        password,
      });
      const data = res.data;
      if (data && typeof data === "object" && data.success === false) {
        throw new Error(data.msg || data.message || "重置失败");
      }
      if (typeof data === "string" && data.indexOf("失败") !== -1) {
        throw new Error(data);
      }
      return data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async pageRoles(params = {}) {
    try {
      const res = await axios.get("/web/role/roleList", {
        params: {
          name: params.name || undefined,
          current: params.current || 1,
          size: params.size || 10,
        },
      });
      return res.data || { records: [], total: 0 };
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async createRole(payload) {
    try {
      const res = await axios.post("/web/role/insertRole", payload);
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async updateRole(payload) {
    try {
      const res = await axios.post("/web/role/updRole", payload);
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async updateRoleAvailable(roleId, available) {
    try {
      const res = await axios.post("/web/role/updAvailable", {
        roleId: String(roleId),
        available: String(available),
      });
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async deleteRole(roleId) {
    try {
      const res = await axios.post(`/web/role/delRole/${roleId}`);
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async listDispatchMenus() {
    try {
      const res = await axios.get("/web/menu/listDispatchMenus");
      return res.data || [];
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async listDispatchRoleMenu(roleId) {
    try {
      const res = await axios.get(`/web/menu/listDispatchRoleMenu/${roleId}`);
      return res.data || [];
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async saveDispatchRoleMenu(roleId, menuIds) {
    try {
      const ids = Array.isArray(menuIds) ? menuIds.join(",") : String(menuIds || "");
      const res = await axios.post("/web/menu/insertDispatchRoleMenu", {
        roleId: String(roleId),
        menuId: ids,
      });
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async listAllRoles() {
    try {
      const res = await axios.get("/web/role/roleList", {
        params: { current: 1, size: 200 },
      });
      return (res.data && res.data.records) || [];
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async listUserRoles(userId) {
    try {
      const res = await axios.get(`/web/role/listUserRoles/${userId}`);
      return res.data || [];
    } catch (error) {
      throw normalizeError(error);
    }
  },

  async assignUserRoles(userId, roleIds) {
    try {
      const res = await axios.post("/web/role/assignUserRoles", {
        userId,
        roleIds: Array.isArray(roleIds) ? roleIds : [],
      });
      if (res.data !== "分配成功") {
        throw new Error(res.data || "分配失败");
      }
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },
};

export const ORDER_STATUS = {
  pending: { label: "待指派", color: "red" },
  pending_confirm: { label: "待确认", color: "orange" },
  serving: { label: "服务中", color: "green" },
  done: { label: "已完工", color: "blue" },
};
